import React, { useEffect, useMemo, useRef, useState } from "react";

interface IFrameRendererProps {
    /** HTML content to render (used with blob URL) */
    htmlContent?: string;
    /** URL to fetch HTML content from (alternative to htmlContent) */
    src?: string;
    /** Whether to automatically size the iframe to fit its content. Default: true */
    /** Whether to automatically size the iframe to fit its content. */
    autoSize?: {
        scrollableParentHeight?: string | number;
        scrollableParentOffset?: number;
        stickyOffset?: number;
    };
    /**
     * Sandbox permissions for the iframe.
     * Default: "allow-same-origin allow-scripts" (allows scripts but restricts forms, popups, top navigation, etc.)
     * Note: "allow-same-origin" is required for reading iframe content (auto-sizing)
     * For more restrictive: remove "allow-scripts" to disable all JavaScript
     */
    sandbox?: string;
}

const addPxSuffixIfNum = (l: string | number) => {
    const suffix = typeof l === "number" ? "px" : "";
    return l + suffix;
};

const applyAutoSizeDefaults = (
    autoSize: Required<IFrameRendererProps>["autoSize"]
): Required<Required<IFrameRendererProps>["autoSize"]> => {
    let { scrollableParentHeight, scrollableParentOffset, stickyOffset } = {
        scrollableParentHeight: "100vh",
        scrollableParentOffset: 0,
        stickyOffset: 0,
        ...autoSize,
    };

    // given parent offset without height, compute height
    if (
        autoSize.scrollableParentHeight === undefined &&
        autoSize.scrollableParentOffset !== undefined
    ) {
        scrollableParentHeight = `calc(100vh - ${scrollableParentOffset}px)`;
    }

    return { scrollableParentHeight, scrollableParentOffset, stickyOffset };
};

export const IFrameRenderer: React.FC<IFrameRendererProps> = ({
    htmlContent,
    src,
    autoSize: _autoSize,
    sandbox = "allow-same-origin allow-scripts",
}) => {
    const autoSize = useMemo(() => _autoSize && applyAutoSizeDefaults(_autoSize), [_autoSize]);

    const iframeRef = useRef<HTMLIFrameElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [contentHeight, setContentHeight] = useState<number | null>(null);

    // Create blob URL from HTML content (if provided)
    const blobUrl = useMemo(() => {
        if (!htmlContent) return null;
        const blob = new Blob([htmlContent], { type: "text/html" });
        return URL.createObjectURL(blob);
    }, [htmlContent]);

    // Use either src prop or blobUrl
    const iframeSrc = src || blobUrl;

    // Cleanup: revoke the blob URL when component unmounts or URL changes
    useEffect(() => {
        if (blobUrl) {
            return () => {
                URL.revokeObjectURL(blobUrl);
            };
        }
    }, [blobUrl]);

    // Measure content height and set up virtual scrolling
    useEffect(() => {
        const iframe = iframeRef.current;
        const wrapper = wrapperRef.current;
        if (!iframe || !wrapper) return;

        if (!autoSize && iframeSrc) {
            iframe.src = iframeSrc;
            return;
        }

        let resizeObserver: ResizeObserver | null = null;
        let targetHeight: number | null = null;
        let rafId: number | null = null;

        const updateSize = () => {
            try {
                const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
                if (!iframeDoc || !iframeDoc.body) return;

                // Get the full height of the body content
                const newContentHeight = Math.max(
                    iframeDoc.body.scrollHeight,
                    iframeDoc.body.offsetHeight,
                    iframeDoc.documentElement.scrollHeight
                );

                // Only update if height actually changed from the last height we set
                // This prevents redundant updates when ResizeObserver fires after we change the iframe height
                if (newContentHeight === targetHeight) {
                    return;
                }

                // Use requestAnimationFrame to avoid ResizeObserver loop warnings
                // This defers the state update to the next frame
                if (rafId !== null) {
                    cancelAnimationFrame(rafId);
                }

                targetHeight = newContentHeight;
                rafId = requestAnimationFrame(() => {
                    setContentHeight(newContentHeight);
                    rafId = null;
                });
            } catch (error) {
                // Cross-origin access might fail, ignore
                console.warn("Unable to access iframe content for sizing:", error);
            }
        };

        // Initial size update after iframe loads
        const handleLoad = () => {
            try {
                const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
                if (!iframeDoc || !iframeDoc.body) return;

                // Inject CSS to hide scrollbars but allow scrolling
                const style = iframeDoc.createElement("style");
                style.textContent = `
                    html {
                        overflow-y: scroll !important;
                        scrollbar-width: none !important; /* Firefox */
                        -ms-overflow-style: none !important; /* IE/Edge */
                        scroll-behavior: auto !important; /* Disable smooth scrolling */
                    }
                    html::-webkit-scrollbar {
                        display: none !important; /* Chrome/Safari */
                    }
                    body {
                        margin: 0 !important;
                        padding: 0 !important;
                    }
                `;
                iframeDoc.head.appendChild(style);

                updateSize();

                // Set up ResizeObserver to watch for content changes
                resizeObserver = new ResizeObserver(() => {
                    updateSize();
                });

                // Only observe body, not documentElement, to avoid feedback loop
                // documentElement resizes when iframe viewport changes, causing infinite updates
                resizeObserver.observe(iframeDoc.body);
            } catch (error) {
                console.warn("Unable to set up ResizeObserver:", error);
            }
        };

        if (iframeSrc) {
            iframe.src = iframeSrc;
        }
        iframe.addEventListener("load", handleLoad);

        return () => {
            iframe.removeEventListener("load", handleLoad);
            resizeObserver?.disconnect();
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
            }
        };
    }, [autoSize, iframeSrc]);

    // Forward iframe scroll events to parent and sync scroll position
    useEffect(() => {
        if (!autoSize) return;

        const wrapper = wrapperRef.current;
        const iframe = iframeRef.current;
        if (!wrapper || !iframe) return;

        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!iframeDoc || !iframeDoc.documentElement) return;

        // Find the scrollable container - walk up the DOM tree
        let scrollContainer: Element | Window = window;
        let parent = wrapper.parentElement;
        while (parent) {
            const overflow = window.getComputedStyle(parent).overflow;
            const overflowY = window.getComputedStyle(parent).overflowY;
            if (
                overflow === "auto" ||
                overflow === "scroll" ||
                overflowY === "auto" ||
                overflowY === "scroll"
            ) {
                scrollContainer = parent;
                break;
            }
            parent = parent.parentElement;
        }

        // Parent scrolls -> update iframe
        const handleParentScroll = () => {
            const rect = wrapper.getBoundingClientRect();
            const scrollOffset = Math.max(
                0,
                autoSize.scrollableParentOffset + autoSize.stickyOffset - rect.top
            );
            iframeDoc.documentElement.scrollTop = scrollOffset;
        };

        // Handle fragment navigation (clicking links like #section-2)
        const handleIframeClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const anchor = target.closest('a[href^="#"]') as HTMLAnchorElement;

            if (!anchor) return;

            e.preventDefault();

            const hash = anchor.getAttribute("href");
            if (!hash || hash === "#") return;

            const targetId = hash.slice(1);
            const targetElement = iframeDoc.getElementById(targetId);

            if (!targetElement) return;

            // Get the element's position within the iframe document
            const elementTop =
                targetElement.getBoundingClientRect().top + iframeDoc.documentElement.scrollTop;

            // Get the wrapper's absolute position in the document
            // offsetTop gives us the position relative to the offsetParent, so we need to walk up
            let wrapperAbsoluteTop = 0;
            let el: HTMLElement | null = wrapper;
            while (el) {
                wrapperAbsoluteTop += el.offsetTop;
                el = el.offsetParent as HTMLElement | null;
            }

            const targetParentScroll =
                wrapperAbsoluteTop +
                elementTop -
                autoSize.scrollableParentOffset -
                autoSize.stickyOffset;

            console.log("[IFrameRenderer] Fragment navigation:", {
                targetId,
                elementTop,
                wrapperAbsoluteTop,
                targetParentScroll,
                currentScroll:
                    scrollContainer instanceof Window ? window.scrollY : scrollContainer.scrollTop,
            });

            // Scroll the parent page (this will trigger handleParentScroll which will sync the iframe)
            if (scrollContainer instanceof Window) {
                window.scrollTo({ top: targetParentScroll, behavior: "smooth" });
            } else {
                scrollContainer.scrollTo({ top: targetParentScroll, behavior: "smooth" });
            }
        };

        // Find scrollable container that can scroll in the given direction
        const findScrollableContainer = (
            element: Element | null,
            deltaY: number
        ): Element | null => {
            if (!element || element === iframeDoc.documentElement || element === iframeDoc.body) {
                return null;
            }

            const style = iframeDoc.defaultView?.getComputedStyle(element);
            if (!style) return null;

            const hasScrollOverflow =
                style.overflow === "auto" ||
                style.overflow === "scroll" ||
                style.overflowY === "auto" ||
                style.overflowY === "scroll";

            const hasOverflowingContent = element.scrollHeight > element.clientHeight;

            if (hasScrollOverflow && hasOverflowingContent) {
                // Check if we can actually scroll in the requested direction
                // Use a small tolerance (1px) to account for floating point precision issues
                const tolerance = 1;
                const canScrollUp = element.scrollTop > tolerance;
                const maxScrollTop = element.scrollHeight - element.clientHeight;
                const canScrollDown = element.scrollTop < maxScrollTop - tolerance;

                console.log("[IFrameRenderer] Found scrollable container:", {
                    element: element.tagName,
                    className: element.className,
                    scrollTop: element.scrollTop,
                    scrollHeight: element.scrollHeight,
                    clientHeight: element.clientHeight,
                    maxScrollTop,
                    distanceFromBottom: maxScrollTop - element.scrollTop,
                    deltaY,
                    canScrollUp,
                    canScrollDown,
                    willHandle: (deltaY < 0 && canScrollUp) || (deltaY > 0 && canScrollDown),
                });

                // Only handle if we can actually scroll in the requested direction
                if ((deltaY < 0 && canScrollUp) || (deltaY > 0 && canScrollDown)) {
                    return element;
                }

                // Log when we're NOT handling despite finding scrollable container
                console.log(
                    "[IFrameRenderer] Not handling - at scroll boundary, will bubble to parent"
                );
            }

            return findScrollableContainer(element.parentElement, deltaY);
        };

        // Prevent native scrolling in iframe and forward to parent (unless in scrollable container)
        const handleIframeWheel = (e: WheelEvent) => {
            // Always prevent default to avoid any native iframe scrolling
            e.preventDefault();

            // Check if event target is within a scrollable container
            const target = e.target as Element;
            const scrollableContainer = findScrollableContainer(target, e.deltaY);

            if (scrollableContainer) {
                // Manually scroll the container instead of letting it handle naturally
                scrollableContainer.scrollLeft += e.deltaX;
                scrollableContainer.scrollTop += e.deltaY;
                return;
            }

            // Forward to parent
            if (scrollContainer instanceof Window) {
                window.scrollBy(e.deltaX, e.deltaY);
            } else {
                scrollContainer.scrollLeft += e.deltaX;
                scrollContainer.scrollTop += e.deltaY;
            }
        };

        const handleIframeKeydown = (e: KeyboardEvent) => {
            const scrollKeys = ["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " "];
            if (!scrollKeys.includes(e.key)) return;

            let delta = 0;
            switch (e.key) {
                case "ArrowUp":
                    delta = -40;
                    break;
                case "ArrowDown":
                    delta = 40;
                    break;
                case "PageUp":
                    delta = -(iframe?.clientHeight || 500);
                    break;
                case "PageDown":
                case " ":
                    delta = iframe?.clientHeight || 500;
                    break;
                case "Home":
                case "End":
                    // For Home/End, we can use a large negative/positive value
                    delta = e.key === "Home" ? -999999 : 999999;
                    break;
            }

            // Check if event target is within a scrollable container
            const target = e.target as Element;
            const scrollableContainer = findScrollableContainer(target, delta);

            if (scrollableContainer) {
                // Let the scrollable container handle it naturally
                return;
            }

            e.preventDefault();

            // Handle Home/End specially
            if (e.key === "Home") {
                if (scrollContainer instanceof Window) {
                    window.scrollTo(0, 0);
                } else {
                    scrollContainer.scrollTop = 0;
                }
                return;
            }
            if (e.key === "End") {
                if (scrollContainer instanceof Window) {
                    window.scrollTo(0, document.documentElement.scrollHeight);
                } else {
                    scrollContainer.scrollTop = scrollContainer.scrollHeight;
                }
                return;
            }

            if (scrollContainer instanceof Window) {
                window.scrollBy(0, delta);
            } else {
                scrollContainer.scrollTop += delta;
            }
        };

        // Listen to parent scroll and forward events from iframe
        scrollContainer.addEventListener("scroll", handleParentScroll, { passive: true });
        iframeDoc.addEventListener("wheel", handleIframeWheel, { passive: false });
        iframeDoc.addEventListener("keydown", handleIframeKeydown);
        iframeDoc.addEventListener("click", handleIframeClick);

        handleParentScroll(); // Initial position

        return () => {
            scrollContainer.removeEventListener("scroll", handleParentScroll);
            iframeDoc.removeEventListener("wheel", handleIframeWheel);
            iframeDoc.removeEventListener("keydown", handleIframeKeydown);
            iframeDoc.removeEventListener("click", handleIframeClick);
        };
    }, [autoSize, contentHeight]);

    return (
        <div
            ref={wrapperRef}
            style={{
                width: "100%",
                height: contentHeight ? `${contentHeight}px` : "100%",
                position: "relative",
            }}
        >
            <div
                style={
                    autoSize
                        ? {
                              position: "sticky",
                              top: autoSize.stickyOffset,
                              height: `calc(${addPxSuffixIfNum(autoSize.scrollableParentHeight)} - ${autoSize.stickyOffset}px)`,
                          }
                        : { height: "100%" }
                }
            >
                <iframe
                    ref={iframeRef}
                    sandbox={sandbox}
                    style={{
                        width: "100%",
                        height: "100%",
                        border: "none",
                        display: "block",
                    }}
                    title="HTML Content"
                />
            </div>
        </div>
    );
};
